import { createTheme } from '@mui/material/styles';

import breakpoints from 'utils/breakpoints';
import { getColor } from 'utils/colors';
import mixins from 'utils/mixins';
import { remCalc } from 'utils/remCalc';

const theme = createTheme({
	breakpoints: breakpoints,
	mixins: mixins,
});

const activeCardStyles = {
	'& .neighborhoodsMap__cardBody': {
		opacity: 0,
	},
	'&.neighborhoodsMap__card--withImage::before': {
		opacity: 0,
	},
	'&.neighborhoodsMap__card--noImage::before': {
		backgroundColor: getColor('ivory', 600),
		opacity: 1,
	},
	'& .MuiTypography-root': {
		color: getColor('white', 900),
	},
};

export const styles = {};

export const overrides = {
	'.neighborhoodsMap': {
		position: 'relative',
		[theme.breakpoints.down('sm')]: {
			rowGap: 0,
			backgroundColor: '#abe6f2',
		},
		marginBottom: remCalc(50),
		'&.margin-flush': {
			marginBottom: 0,
		},

		'.neighborhoodsMap__interactive': {
			position: 'relative',
			overflow: 'hidden',
			// Match the SVG viewBox ratio (568.76 / 329.95) so the map isn't letterboxed;
			// min-height leaves room for the slider overlay.
			aspectRatio: '568.76 / 329.95',
			// aspect-ratio + min-height would otherwise derive a min-width and overflow narrow viewports.
			width: '100%',
			minWidth: 0,
			minHeight: remCalc(720),
			[theme.breakpoints.down('sm')]: {
				maxHeight: remCalc(1100),
			},
			'> svg': {
				display: 'block',
				width: '100%',
				height: '100%',
				touchAction: 'auto',
				[theme.breakpoints.down('sm')]: {
					position: 'relative',
					top: '-11rem',
				},
			},
			'&.neighborhoodsMap__interactive--interactive > svg': {
				touchAction: 'none',
			},
			'&::after': {
				content: '""',
				position: 'absolute',
				height: '308px',
				width: '100%',
				right: 0,
				bottom: 0,
				pointerEvents: 'none',
				background: 'linear-gradient(0deg, rgba(250, 250, 250, 1) 0%, rgba(255, 255, 255, 0) 90%)',
				zIndex: 1,
			},
			[theme.breakpoints.down('md')]: {
				'&::before': {
					content: '""',
					position: 'absolute',
					height: '308px',
					width: '100%',
					top: 0,
					left: 0,
					pointerEvents: 'none',
					background: 'linear-gradient(180deg, rgba(250, 250, 250, 1) 0%, rgba(255, 255, 255, 0) 90%)',
					zIndex: 1,
				},
			},
		},

		'.neighborhoodsMap__titleContainer': {
			marginBottom: "var(--space-gap)",
		},

		'.neighborhoodsMap__title': {
			textTransform: 'uppercase',
		},

		'.neighborhoodsMap__pins': {
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			zIndex: 'calc(var(--mui-zIndex-appBar) - 1)',
			height: '100%',
			width: '100%',
			pointerEvents: 'none',
			[theme.breakpoints.down('sm')]: {
				top: '-11rem',
			},
			'> *': {
				opacity: 0,
				animation: 'none',
			},
			'&.neighborhoodsMap__pins--interactive': {
				pointerEvents: 'auto',
				'> *': {
					opacity: 1,
					animation: 'neighborhoodsMapPinFadeIn var(--transition-duration-fast) ease-out both',
				},
			},
		},

		'@keyframes neighborhoodsMapPinFadeIn': {
			from: { opacity: 0, transform: 'translate(-50%, calc(-100% + 8px))' },
			to: { opacity: 1, transform: 'translate(-50%, -100%)' },
		},

		'.neighborhoodsMap__sliderWrap': {
			position: 'absolute',
			bottom: 0,
			width: '100%',
			zIndex: 'calc(var(--mui-zIndex-appBar) - 1)',
			opacity: 0,
			transition: 'all 1s ease-in-out',
		},

		'.neighborhoodsMap__sliderWrap--enteredView': {
			opacity: 1,
			bottom: remCalc(136),
			[theme.breakpoints.down('md')]: {
				bottom: remCalc(80),
			},
		},

		'.neighborhoodsMap__slide > *': {
			width: '100%',
			display: 'flex',
		},

		'.neighborhoodsMap__card': {
			position: 'relative',
			minHeight: remCalc(272),
			border: 'none',
			borderRadius: 0,
			boxShadow: 'none',
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			height: '100%',
			display: 'flex',
			width: '100%',
			'& .MuiCardActionArea-root': {
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'stretch',
			},
			'& .neighborhoodsMap__cardBody': {
				color: getColor('ivory', 700),
				opacity: 1,
				transition: 'opacity 0.25s ease',
				'&.neighborhoodsMap__cardBody--large': {
					fontSize: remCalc(32),
					lineHeight: 1.25,
					fontWeight: 390,
					letterSpacing: remCalc(-0.32),
				},
			},
			'& .neighborhoodsMap__cardTitle': {
				color: getColor('ivory', 800),
				[theme.breakpoints.down('sm')]: {
					marginTop: 'auto',
				},
			},
			'&::before': {
				content: '""',
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				backgroundColor: getColor('white'),
				opacity: 1,
				transition: 'opacity var(--transition-duration) ease, background-color var(--transition-duration) ease',
				zIndex: 1,
			},
			'&.neighborhoodsMap__card--active, &:hover, &:focus-within': activeCardStyles,
			[theme.breakpoints.down('sm')]: activeCardStyles,
		},

		'.neighborhoodsMap__cardContent': {
			padding: remCalc(40),
			display: 'flex',
			gap: remCalc(30),
			flexDirection: 'column',
			// justifyContent: 'space-between',
			alignItems: 'flex-start',
			flex: '1 0 0',
			alignSelf: 'stretch',
			position: 'relative',
			zIndex: 2,
			// height: '100%',
		},

		'.neighborhoodsMap__pin': {
			zIndex: theme.zIndex.modal - 1,
			height: remCalc(52),
			width: remCalc(52),
			minHeight: remCalc(52),
			minWidth: remCalc(52),
			padding: 0,
			borderRadius: '100%',
			border: `1px solid ${getColor('ivory', 600)}`,
			boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.15)',
			backdropFilter: 'blur(15px)',
			backgroundColor: getColor('white', 900),
			transition: 'background-color var(--transition-duration) ease, border-color var(--transition-duration) ease, box-shadow var(--transition-duration) ease',
			'& svg, & path': {
				transition: 'fill var(--transition-duration) ease',
				fill: getColor('ivory', 700),
			},
			'&.neighborhoodsMap__pin--hover, &.neighborhoodsMap__pin--map-focus': {
				backgroundColor: getColor('ivory', 600),
				borderColor: getColor('ivory', 600),
				'& svg, & path': {
					fill: getColor('white', 900),
				},
			},
			'&.neighborhoodsMap__pin--active, &.neighborhoodsMap__pin--card-focus': {
				backgroundColor: getColor('white', 900),
				borderColor: getColor('white', 900),
				'& svg, & path': {
					fill: getColor('ivory', 800),
				},
			},
			'&.neighborhoodsMap__pin--map-icon': {
				height: remCalc(52),
				width: remCalc(52),
				border: `2px solid ${getColor('ivory', 600)}`,
				boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.15)',
				backdropFilter: 'blur(15px)',
				backgroundColor: 'rgba(255, 255, 255, 0.30)',
				'&:hover': {
					border: `2px solid ${getColor('ivory', 700)}`,
					boxShadow: '0 0 4px 0 rgba(0, 0, 0, 0.15)',
				},
				[theme.breakpoints.down('md')]: {
					height: remCalc(52),
					width: remCalc(52),
					minHeight: remCalc(52),
					minWidth: remCalc(52),
				},
			},
			'&.neighborhoodsMap__pin--map-icon.neighborhoodsMap__pin--active': {
				backgroundColor: getColor('ivory', 600),
				borderColor: getColor('ivory', 600),
				'& svg, & path': {
					fill: getColor('white', 900),
				},
			},
		},

		'.cardSlider__navigation': {
			marginBottom: remCalc(15),
			'button': {
				padding: remCalc(6),
				width: remCalc(62),
				height: remCalc(62),
				borderRadius: '6px',
				...mixins.glass,
				'&:hover': {
					backgroundColor: getColor('white'),
				}
			}
		}
	},
};

export default styles;
